import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { COLUMN_ACTION_GROUP } from '../../constants';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { LlmColumnModel } from '../llm-column.model';
import { toast } from '@cotera/client/app/components/ui';
import { Constant } from '@cotera/era';
import { Assert } from '@cotera/utilities';
import { DG } from '@cotera/sdk/core';
import { GraphModel } from '@cotera/client/v0/graph/graph.model';

export class LLMColumnFilterPreviewAction extends BaseAction<
  {},
  { t: string; message?: string }[]
> {
  title = 'Filter Column Preview';
  inputSchema = z.object({});
  shortcut = ['ctrl', 'enter'];
  group = COLUMN_ACTION_GROUP;
  icon = 'filter';

  applicable(context: ApplicableContext): boolean {
    return (
      context.hasTargetedModelOfType(LlmColumnModel) &&
      !context.targetedModelOfType(LlmColumnModel)!.hasErrors.snapshot()
    );
  }

  async execute(
    _: {},
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(LlmColumnModel);
    const graphModel = context.getInScopeModelOfType(GraphModel);

    Assert.assert(graphModel !== null);
    Assert.assert(model !== null);

    const name = model.currentNameInGraph.snapshot();

    // Build the config from current form state (v2)
    const editorState = model.v2EditorState.snapshot();
    const docEditor = new DG.DocumentEditor.EditorState(editorState);

    const expr = docEditor.getExpression(
      graphModel.graph.snapshot(),
      graphModel.graph.snapshot().parser.forRel(model.datasetName)
    );

    if (expr.isErr()) {
      return err([{ t: 'error', message: 'Failed to parse expression' }]);
    }

    const whereExpr = model.whereExpression.snapshot();

    const rel = model.datasetRel.snapshot();

    if (rel === null || rel.isErr()) {
      return err([{ t: 'error', message: 'Failed to get relation' }]);
    }

    Assert.assert(rel.value.t === 'rel');

    toast.success(`Filtering preview for ${name}`);

    const preview = rel.value.rel
      .select((t) => {
        return {
          ...t.star(),
          [`${name}__input`]: expr.value,
        };
      })
      .where((t) => t.attr(`${name}__input`).isNotNull())
      .where((_) => (whereExpr ? whereExpr : Constant(true)))
      .limit(300);

    model.preview.set(preview ?? null);

    return ok({});
  }
}
